import { Fragment, MouseEvent, useCallback, useState } from 'react';
import { Cell, Pie, PieChart } from 'recharts';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { FormApi } from 'final-form';

import { TableWithExternalScroll } from 'UIPackage/MainModule/components/TableWithExternalScroll';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { LoaderWithoutStore } from 'CorePackage/MainModule/components/Loader';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './SmevMonitoringCharts.module.scss';
import {
  getSmevMonitoringAdditionalCharts,
  getSmevMonitoringCharts,
  getSmevMonitoringFilterForm,
  getSmevMonitoringIsLoadingAdditionalCharts,
} from '../selectors/smevMonitoringSelectors';
import { SmevMonitoringChartTypesEnum } from '../enums/SmevMonitoringChartTypesEnum';
import {
  smevMonitoringGetChartsWithEndpoints,
  smevMonitoringGetSystemCharts,
  smevMonitoringGetTableData,
  smevMonitoringResetAdditionalCharts,
  smevMonitoringSetFilter,
} from '../actions/smevMonitoringActions';
import { convertStringToSelectOption } from '../common/convertStringToSelectOption';
import { ISmevMonitoringChartData } from '../interfaces/ISmevMonitoringChartData';
import { ISmevMonitoringFilter } from '../interfaces/ISmevMonitoringFilter';
import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';

const chartCellColors: Record<SmevMonitoringChartTypesEnum, string> = {
  [SmevMonitoringChartTypesEnum.FAILED_BY_SMEV_MESSAGES]: '#f88',
  [SmevMonitoringChartTypesEnum.FAILED_MESSAGES]: '#fe2424',
  [SmevMonitoringChartTypesEnum.SUCCESSFUL_MESSAGES]: '#5cb85c',
};

interface ISmevMonitoringChartsStoreProps {
  isLoadingAdditionalCharts: boolean;
  groupedCharts: {
    [key: string]: ISmevMonitoringChartData[] | undefined;
  };
  groupedAdditionalCharts: {
    [key: string]: ISmevMonitoringChartData[];
  };
  filterForm: FormApi<ISmevMonitoringFilter, Partial<ISmevMonitoringFilter>> | undefined;
}

const SmevMonitoringChartsWithoutStore = ({
  filterForm,
  groupedAdditionalCharts,
  groupedCharts,
  isLoadingAdditionalCharts,
}: ISmevMonitoringChartsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const namesByCharts = Object.keys(groupedCharts);
  const endpointsByAdditionalCharts = Object.keys(groupedAdditionalCharts);
  const activeAdditionalCharts: ISmevMonitoringChartData | undefined = groupedAdditionalCharts[
    endpointsByAdditionalCharts[0]
  ]?.find(item => item.systemType);
  const [isOpen, setIsOpen] = useState(false);
  const renderLegendItemClassNames = (type: SmevMonitoringChartTypesEnum) => {
    return {
      ...(type === SmevMonitoringChartTypesEnum.FAILED_MESSAGES && { 'data-failed': true }),
      ...(type === SmevMonitoringChartTypesEnum.SUCCESSFUL_MESSAGES && { 'data-success': true }),
      ...(type === SmevMonitoringChartTypesEnum.FAILED_BY_SMEV_MESSAGES && { 'data-smev': true }),
    };
  };

  const handleItemClick = (systemType?: string, serviceType?: string, endpoint?: string) => (
    e: MouseEvent<HTMLDivElement>,
  ) => {
    if (!(e.target instanceof HTMLSpanElement)) {
      dispatch(smevMonitoringSetFilter({ system: systemType, service: serviceType, endpoint }));
      dispatch(smevMonitoringGetTableData());
      if (filterForm) {
        systemType && filterForm.change('system', convertStringToSelectOption(systemType));
        serviceType && filterForm.change('service', convertStringToSelectOption(serviceType));
        endpoint ? filterForm.change('endpoint', convertStringToSelectOption(endpoint)) : filterForm.change('endpoint');
      }
    }
  };

  const handleResetAdditionalCharts = useCallback(() => {
    dispatch(smevMonitoringResetAdditionalCharts());
  }, [dispatch]);

  const handleGetDetailCharts = (system?: string, service?: string) => () => {
    if (system && service) {
      dispatch(smevMonitoringGetChartsWithEndpoints(system, service));
    }
  };

  const handleSmevMonitoringChartsInit = useCallback(() => {
    if (!isOpen) {
      dispatch(smevMonitoringGetSystemCharts());
    }
    setIsOpen(!isOpen);
  }, [dispatch, setIsOpen, isOpen]);

  return (
    <KnmCollapse title="Диаграммы" onToggle={handleSmevMonitoringChartsInit} opened={isOpen}>
      <div className={styles.Container}>
        {!namesByCharts?.length && (
          <div style={{ height: '150px' }}>
            <LoaderWithoutStore position="static" loading />
          </div>
        )}
        {namesByCharts.map(name => {
          const chart = groupedCharts[name]?.find(item => item.systemType);
          const serviceTypeItemAttributes = {
            ...(groupedCharts[name]?.find(item => item.isActive) && { 'data-active': true }),
          };

          return (
            <div key={name} className={styles.ServiceTypeItem} {...serviceTypeItemAttributes}>
              <div className={styles.ChartLabel}>
                <button
                  className={styles.ServiceType}
                  disabled={isLoadingAdditionalCharts}
                  onClick={handleGetDetailCharts(chart?.systemType, chart?.serviceType)}
                >
                  {name}
                </button>
              </div>
              <div className={styles.MonitoringCharts}>
                {groupedCharts[name]
                  ?.filter(item => !item.isAdditional)
                  .map((item, index) => {
                    const chartItemAttributes = {
                      ...(item.isLoading && { 'data-loading': true }),
                    };

                    return (
                      <div
                        className={styles.ChartItem}
                        key={index}
                        onClick={handleItemClick(item.systemType, item.serviceType)}
                        {...chartItemAttributes}
                      >
                        {!item.isLoading ? (
                          <Fragment>
                            <div className={styles.ChartInfo}>{index ? '4 часа' : '3 дня'}</div>
                            {(item.isActive && (
                              <>
                                <PieChart width={140} height={140} className={styles.ChartWrapper}>
                                  <Pie
                                    data={item.list}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label
                                    fill="#8884d8"
                                    dataKey="value"
                                    outerRadius={40}
                                  >
                                    {item.list.map((entry, indexCell) => (
                                      <Cell
                                        className={styles.ChartCell}
                                        key={`cell-${indexCell}`}
                                        fill={chartCellColors[entry.type]}
                                      />
                                    ))}
                                  </Pie>
                                </PieChart>
                                <ul className={styles.ChartLegend}>
                                  {item.list.map((entry, indexCell) => (
                                    <li
                                      className={styles.LegendItem}
                                      key={indexCell}
                                      {...renderLegendItemClassNames(entry.type)}
                                    >
                                      {entry.name}
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )) || <div className={styles.Info}>Нет данных</div>}
                          </Fragment>
                        ) : (
                          <LoaderWithoutStore position="static" loading={item.isLoading} />
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}
        {activeAdditionalCharts?.serviceType && (
          <>
            <div className={styles.Title}>
              Диаграммы&nbsp;
              <span className={styles.AdditionalTitle}>
                {`${activeAdditionalCharts.systemType} ${activeAdditionalCharts.serviceType}`}
              </span>
              {!isLoadingAdditionalCharts && (
                <span className={styles.CancelButton} onClick={handleResetAdditionalCharts} />
              )}
            </div>
            {endpointsByAdditionalCharts.map(endpoint => {
              const serviceTypeItemAttributes = {
                ...(groupedCharts[endpoint]?.find(item => item.isActive) && { 'data-active': true }),
              };

              return (
                <div key={endpoint} className={styles.ServiceTypeItem} {...serviceTypeItemAttributes}>
                  <div className={styles.ChartLabel}>{endpoint}</div>
                  <div className={styles.MonitoringCharts}>
                    {groupedAdditionalCharts[endpoint]
                      .filter(item => item.isAdditional)
                      .map((item, index) => {
                        const chartItemAttributes = {
                          ...(item.isLoading && { 'data-loading': true }),
                        };

                        return (
                          <div
                            className={styles.ChartItem}
                            key={index}
                            onClick={handleItemClick(item.systemType, item.serviceType, item.endpoint)}
                            {...chartItemAttributes}
                          >
                            {!item.isLoading ? (
                              <Fragment>
                                <div className={styles.ChartInfo}>{index ? '4 часа' : '3 дня'}</div>
                                {item.isActive ? (
                                  <>
                                    <PieChart width={140} height={140} className={styles.ChartWrapper}>
                                      <Pie
                                        data={item.list}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label
                                        fill="#8884d8"
                                        dataKey="value"
                                        outerRadius={40}
                                      >
                                        {item.list.map((entry, indexCell) => (
                                          <Cell
                                            className={styles.ChartCell}
                                            key={`cell-${indexCell}`}
                                            fill={chartCellColors[entry.type]}
                                          />
                                        ))}
                                      </Pie>
                                    </PieChart>
                                    <ul className={styles.ChartLegend}>
                                      {item.list.map((entry, indexCell) => (
                                        <li
                                          className={styles.LegendItem}
                                          key={indexCell}
                                          {...renderLegendItemClassNames(entry.type)}
                                        >
                                          {entry.name}
                                        </li>
                                      ))}
                                    </ul>
                                  </>
                                ) : (
                                  <div className={styles.Info}>Нет данных</div>
                                )}
                              </Fragment>
                            ) : (
                              <LoaderWithoutStore position="static" loading={item.isLoading} />
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): ISmevMonitoringChartsStoreProps => ({
  isLoadingAdditionalCharts: getSmevMonitoringIsLoadingAdditionalCharts(state),
  groupedCharts: getSmevMonitoringCharts(state),
  groupedAdditionalCharts: getSmevMonitoringAdditionalCharts(state),
  filterForm: getSmevMonitoringFilterForm(state),
});

const SmevMonitoringChartsWithoutExternalScroll = connect(mapStateToProps)(SmevMonitoringChartsWithoutStore);

export const SmevMonitoringCharts = TableWithExternalScroll(SmevMonitoringChartsWithoutExternalScroll);
